import * as THREE from 'three';
import React, { useMemo, Suspense } from 'react';
import { useFrame } from '@react-three/fiber';
import { useShallow } from 'zustand/react/shallow';
import { Line, Html, Billboard, Text } from '@react-three/drei';
import { RotateCcw, Copy, ArrowLeftRight } from 'lucide-react';
import { useDanceStore, calculateGroundedElevation, MIRROR_MAP, JOINT_PARENT_MAP } from '../store/useDanceStore';
import { useStorageStore } from '../store/useStorageStore';
import { TUTORIAL_MODULES } from '../utils/tutorialData';
import { DanceState, Pose, StorageState, JointRotation } from '../types';
import { JOINT_CONSTRAINTS, getJointRotationOrder } from '../utils/constraints';

import { MenuItem } from './ContextMenu';
import { useTranslation } from 'react-i18next';
import { trackEditorEvent } from '../lib/analytics';
import { MANNEQUIN_DIMENSIONS } from '../utils/dimensions';
import { MannequinClothing } from './ProceduralClothing';

// Visual constants for the "SVG-like" look
const JOINT_RADIUS = 0.2;
const PRIMARY_COLOR = '#f3cfb3'; // Warmer, more natural skin tone
const SECONDARY_COLOR = '#e5b08b'; // Soft shadow/joint tone
const HI_COLOR = '#ffe5d9'; // Highlight skin tone for chest/core
const SELECTED_COLOR = '#38bdf8'; // Sky blue for selection (contrasts well with skin)
const JOINT_COLOR = '#6b4423'; // Darker brown for joint distinction
const HAIR_COLOR = '#3d2e25'; // Dark brown hair
const HOVER_GOLD = '#ffd700'; // Dramatic Gold for hover
const SKIN_PROPS = {
  roughness: 0.85,    // High roughness for matte skin
  metalness: 0.05,    // Tiny bit of metalness can sometimes help with skin sheen, but usually 0
  envMapIntensity: 0.1, // Subdue harsh environment reflections
  sheen: 0.4,         // Soft velvet-like sheen for micro-hairs
  sheenRoughness: 0.8,
  sheenColor: '#ffffff'
};
const HAIR_PROPS = {
  roughness: 0.9,
  metalness: 0.0,
  envMapIntensity: 0.5,
  sheen: 0.1,
  sheenRoughness: 0.9,
  sheenColor: '#241c19'
};

// Reusable Geometries and Materials to prevent allocation in render loop
const SPHERE_GEO = new THREE.SphereGeometry(1, 24, 24);
const CYLINDER_GEO = new THREE.CylinderGeometry(1, 1, 1, 16);
const TORUS_GEO = new THREE.TorusGeometry(1, 0.02, 16, 32);
const RING_GEO = new THREE.RingGeometry(1, 1.1, 64);

const SKIN_MAT = new THREE.MeshPhysicalMaterial({ color: PRIMARY_COLOR, ...SKIN_PROPS });
const JOINT_MAT = new THREE.MeshPhysicalMaterial({ color: JOINT_COLOR, ...SKIN_PROPS });
const HI_MAT = new THREE.MeshPhysicalMaterial({ color: HI_COLOR, ...SKIN_PROPS });
const HAIR_MAT = new THREE.MeshPhysicalMaterial({ color: HAIR_COLOR, ...HAIR_PROPS });
const SELECTED_MAT = new THREE.MeshStandardMaterial({ color: SELECTED_COLOR, roughness: 0.5, metalness: 0.2 });
const PARENT_HIGHLIGHT_COLOR = '#f87171'; // Red highlight
const PARENT_HIGHLIGHT_MAT = new THREE.MeshStandardMaterial({ color: PARENT_HIGHLIGHT_COLOR, roughness: 0.5, metalness: 0.2 });
const HOVER_MAT = new THREE.MeshStandardMaterial({ color: HOVER_GOLD, emissive: HOVER_GOLD, emissiveIntensity: 0.8, roughness: 0.2, metalness: 0.6 });
const HOVER_INDICATOR_MAT = new THREE.MeshBasicMaterial({ color: HOVER_GOLD, transparent: true, opacity: 0.7, side: THREE.DoubleSide, depthTest: false });
const HOVER_GLOW_MAT = new THREE.MeshBasicMaterial({ color: HOVER_GOLD, transparent: true, opacity: 0.1, depthWrite: false });

interface ProxyBoneProps {
  from?: [number, number, number];
  to?: [number, number, number];
  length?: number;
  color?: string;
  radiusTop?: number;
  radiusBottom?: number;
}

export const ProxyBone: React.FC<ProxyBoneProps> = React.memo(({ 
  from = [0, 0, 0], 
  to = [0, 0, 0], 
  length, 
  color = SECONDARY_COLOR, 
  radiusTop = 0.1, 
  radiusBottom = 0.1 
}) => {
  const { center, quaternion, len } = useMemo(() => {
    const fromV = new THREE.Vector3(...from);
    const toV = new THREE.Vector3(...to);
    const direction = new THREE.Vector3().subVectors(toV, fromV);
    const l = length ?? direction.length();
    const c = new THREE.Vector3().addVectors(fromV, toV).multiplyScalar(0.5);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.clone().normalize());
    return { center: c.toArray(), quaternion: q, len: l };
  }, [from, to, length]);

  // Create a tapered geometry only when radii change (which they don't during posing)
  // We make it 1 unit high so we can scale the height (len) without disposing the geometry
  const geometry = useMemo(() => new THREE.CylinderGeometry(radiusTop, radiusBottom, 1, 16), [radiusTop, radiusBottom]);

  return (
    <mesh 
      position={center as [number, number, number]} 
      quaternion={quaternion} 
      scale={[1, len, 1]}
      geometry={geometry}
    >
      <meshPhysicalMaterial color={color} {...SKIN_PROPS} />
    </mesh>
  );
});

const PelvicTriangle: React.FC<{ hipX: number; apexY: number }> = React.memo(({ hipX, apexY }) => {
  const geometry = useMemo(() => {
    const thickness = 0.16;
    const shape = new THREE.Shape();
    shape.moveTo(hipX, 0);
    shape.lineTo(-hipX, 0);
    shape.lineTo(0, apexY);
    shape.closePath();
    return new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false });
  }, [hipX, apexY]);

  return (
    <mesh geometry={geometry} position={[0, 0, -0.08]}>
      <meshPhysicalMaterial color={SECONDARY_COLOR} {...SKIN_PROPS} />
    </mesh>
  );
});

const getMirroredJoint = (joint: string | null | undefined): string | null => {
  if (!joint) return null;
  if (joint.startsWith('left')) return joint.replace('left', 'right');
  if (joint.startsWith('right')) return joint.replace('right', 'left');
  return null;
};

interface ProxyJointProps {
  position?: [number, number, number];
  color?: string;
  size?: number;
  jointName?: string;
  hideGizmo?: boolean;
}

export const ProxyJoint: React.FC<ProxyJointProps & { 
  selectedJoint?: string | null; 
  setSelectedJoint?: (joint: string | null) => void; 
  isMirroring?: boolean;
  isTutorialTarget?: boolean;
}> = React.memo(({ 
  position = [0, 0, 0], 
  color = JOINT_COLOR, 
  size = JOINT_RADIUS, 
  jointName, 
  hideGizmo, 
  selectedJoint, 
  setSelectedJoint,
  isMirroring,
  isTutorialTarget: propIsTutorialTarget
}) => {
  const [hovered, setHovered] = React.useState(false);
  const { t } = useTranslation();
  const setIsHoveringJoint = useDanceStore(state => state.setIsHoveringJoint);
  const openContextMenu = useDanceStore(state => state.openContextMenu);
  const resetJoint = useDanceStore(state => state.resetJoint);
  const copyJointToOpposite = useDanceStore(state => state.copyJointToOpposite);
  const copyJointFromOpposite = useDanceStore(state => state.copyJointFromOpposite);
  const isReadOnly = useDanceStore(state => state.isReadOnly);
  const startDirectDrag = useDanceStore(state => state.startDirectDrag);
  const showAxes = useDanceStore((state: DanceState) => state.showAxes);
  
  const mirroredJoint = isMirroring ? getMirroredJoint(selectedJoint) : null;
  const isSelected = !!(jointName && (selectedJoint === jointName || mirroredJoint === jointName));
  const isParentOfSelected = selectedJoint ? JOINT_PARENT_MAP[selectedJoint] === jointName : false;
  const isParentOfMirrored = (isMirroring && mirroredJoint) ? JOINT_PARENT_MAP[mirroredJoint] === jointName : false;
  const isAffectedParent = isParentOfSelected || isParentOfMirrored;

  const isTightArea = jointName === 'neck' || jointName?.includes('Clavicle');
  const interactionSize = isTightArea ? size * 1.3 : size * 2.0;

  const isTutorialTarget = !!propIsTutorialTarget;

  return (
    <group name={jointName ? `proxy-${jointName}` : undefined} position={position as [number, number, number]}>
      {/* Visual Joint */}
      <mesh scale={size} geometry={SPHERE_GEO}>
        {isSelected ? (
          <primitive object={SELECTED_MAT} attach="material" />
        ) : isAffectedParent ? (
          <primitive object={PARENT_HIGHLIGHT_MAT} attach="material" />
        ) : hovered ? (
          <primitive object={HOVER_MAT} attach="material" />
        ) : (
          <meshPhysicalMaterial color={color} {...SKIN_PROPS} />
        )}
      </mesh>

      {/* Expanded Interaction Volume & Hover Feedback - Only if it's an actual joint */}
      {jointName && (
        <mesh
          scale={interactionSize}
          geometry={SPHERE_GEO}
          onPointerDown={(e) => {
            if (isReadOnly) return;
            const parentJoint = JOINT_PARENT_MAP[jointName];
            if (!parentJoint) return;
            e.stopPropagation();
            if (setSelectedJoint) {
              setSelectedJoint(jointName);
            }
            const worldPos = new THREE.Vector3();
            e.eventObject.getWorldPosition(worldPos);
            startDirectDrag(jointName, parentJoint, e.clientX, e.clientY, [worldPos.x, worldPos.y, worldPos.z]);
          }}
          onPointerEnter={(e) => {
            e.stopPropagation();
            if (isReadOnly) return;
            setHovered(true);
            setIsHoveringJoint(true);
          }}
          onPointerLeave={(e) => {
            if (isReadOnly) return;
            setHovered(false);
            setIsHoveringJoint(false);
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (isReadOnly) return;
            if (setSelectedJoint) {
              setSelectedJoint(jointName);
              trackEditorEvent('joint_selected_3d', { joint: jointName });
            }
          }}
          onContextMenu={(e) => {
            e.stopPropagation();
            if (isReadOnly) return;
            const originalEvent = (e as any).nativeEvent || e;
            const menuItems: MenuItem[] = [
              { label: t('sidebar.reset_joint'), icon: RotateCcw, action: () => {
                resetJoint(jointName);
                trackEditorEvent('joint_context_action', { joint: jointName, action: 'reset' });
              } }
            ];
            if (MIRROR_MAP[jointName]) {
              menuItems.push({ isDivider: true });
              menuItems.push({ label: t('sidebar.copy_to_opposite'), icon: Copy, action: () => {
                copyJointToOpposite(jointName);
                trackEditorEvent('joint_context_action', { joint: jointName, action: 'copy_to' });
              } });
              menuItems.push({ label: t('sidebar.copy_from_opposite'), icon: ArrowLeftRight, action: () => {
                copyJointFromOpposite(jointName);
                trackEditorEvent('joint_context_action', { joint: jointName, action: 'copy_from' });
              } });
            }
            openContextMenu(originalEvent.clientX, originalEvent.clientY, menuItems);
          }}
        >
          <meshBasicMaterial transparent opacity={0} />
        </mesh>
      )}

      {/* Hover Visual Indicator Circle/Volume - Only if it's an actual joint */}
      {hovered && jointName && (
        <group>
          <Billboard>
            <mesh scale={interactionSize} geometry={RING_GEO}>
              <primitive object={HOVER_INDICATOR_MAT} attach="material" />
            </mesh>
          </Billboard>
          <mesh scale={interactionSize} geometry={SPHERE_GEO}>
            <primitive object={HOVER_GLOW_MAT} attach="material" />
          </mesh>
        </group>
      )}
      
      {isTutorialTarget && (
        <group>
          <Html center pointerEvents="none" style={{ pointerEvents: 'none' }}>
             <div id="tutorial-3d-target" style={{ width: '1px', height: '1px', pointerEvents: 'none' }} />
          </Html>
          <mesh rotation={[Math.PI / 2, 0, 0]} scale={size * 1.5} raycast={() => null}>
            <primitive object={TORUS_GEO} attach="geometry" />
            <meshBasicMaterial color="#f59e0b" transparent opacity={0.8} />
          </mesh>
        </group>
      )}

      {jointName === 'torso' && (
        <group name="torso-disc" rotation-x={-Math.PI / 2}>
          <mesh scale={2.2}>
            <circleGeometry args={[1, 64]} />
            <meshBasicMaterial color={SELECTED_COLOR} transparent opacity={0.15} side={THREE.DoubleSide} />
          </mesh>
          <mesh scale={2.2}>
            <ringGeometry args={[0.99, 1.01, 64]} />
            <meshBasicMaterial color={SELECTED_COLOR} transparent opacity={0.4} />
          </mesh>
        </group>
      )}

      {isSelected && !hideGizmo && showAxes && (
        <Suspense fallback={null}>
          <LocalAxesWidget jointName={jointName} />
        </Suspense>
      )}
    </group>
  );
});

interface ProxyOvalProps {
  scale: [number, number, number];
  offset?: [number, number, number];
  separationAxis?: 'Y' | 'Z';
  colorFront?: string;
  colorBack?: string;
  jointName?: string;
  hideGizmo?: boolean;
}

export const ProxyOval: React.FC<ProxyOvalProps & { 
  selectedJoint?: string | null; 
  setSelectedJoint?: (joint: string | null) => void; 
  isMirroring?: boolean;
  isTutorialTarget?: boolean;
}> = React.memo(({ 
  scale, 
  offset = [0, 0, 0], 
  separationAxis = 'Z', 
  colorFront = PRIMARY_COLOR, 
  colorBack = SECONDARY_COLOR, 
  jointName, 
  hideGizmo, 
  selectedJoint, 
  setSelectedJoint,
  isMirroring,
  isTutorialTarget
}) => {
  const [hovered, setHovered] = React.useState(false);
  const setIsHoveringJoint = useDanceStore(state => state.setIsHoveringJoint);
  const openContextMenu = useDanceStore(state => state.openContextMenu);
  const resetJoint = useDanceStore(state => state.resetJoint);
  const copyJointToOpposite = useDanceStore(state => state.copyJointToOpposite);
  const copyJointFromOpposite = useDanceStore(state => state.copyJointFromOpposite);
  const isReadOnly = useDanceStore(state => state.isReadOnly);
  const startDirectDrag = useDanceStore(state => state.startDirectDrag);
  const showAxes = useDanceStore((state: DanceState) => state.showAxes);
  const mirroredJoint = isMirroring ? getMirroredJoint(selectedJoint) : null;
  const isSelected = !!(jointName && (selectedJoint === jointName || mirroredJoint === jointName));
  const isParentOfSelected = selectedJoint ? JOINT_PARENT_MAP[selectedJoint] === jointName : false;
  const isParentOfMirrored = (isMirroring && mirroredJoint) ? JOINT_PARENT_MAP[mirroredJoint] === jointName : false;
  const isAffectedParent = isParentOfSelected || isParentOfMirrored;
  const isY = separationAxis === 'Y';
  const posFront: [number, number, number] = isY ? [0, 0.05, 0] : [0, 0, 0.05];
  const posBack: [number, number, number] = isY ? [0, -0.05, 0] : [0, 0, -0.05];

  return (
    <group
      position={offset as [number, number, number]}
      onPointerDown={(e) => {
        if (isReadOnly || !jointName) return;
        const parentJoint = JOINT_PARENT_MAP[jointName];
        if (!parentJoint) return;
        e.stopPropagation();
        if (setSelectedJoint) {
          setSelectedJoint(jointName);
        }
        const worldPos = new THREE.Vector3();
        e.eventObject.getWorldPosition(worldPos);
        startDirectDrag(jointName, parentJoint, e.clientX, e.clientY, [worldPos.x, worldPos.y, worldPos.z]);
      }}
      onPointerEnter={(e) => {
        e.stopPropagation();
        if (isReadOnly) return;
        setHovered(true);
        setIsHoveringJoint(true);
      }}
      onPointerLeave={(e) => {
        if (isReadOnly) return;
        setHovered(false);
        setIsHoveringJoint(false);
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (isReadOnly) return;
        if (jointName && setSelectedJoint) {
          setSelectedJoint(jointName);
          trackEditorEvent('joint_selected_3d', { joint: jointName });
        }
      }}
      onContextMenu={(e) => {
        e.stopPropagation();
        if (isReadOnly) return;
        if (!jointName) return;
        const originalEvent = (e as any).nativeEvent || e;
        const menuItems: MenuItem[] = [
          { label: 'Reset Joint', icon: RotateCcw, action: () => {
            resetJoint(jointName);
            trackEditorEvent('joint_context_action', { joint: jointName, action: 'reset' });
          } }
        ];
        if (MIRROR_MAP[jointName]) {
          menuItems.push({ isDivider: true });
          menuItems.push({ label: 'Copy to Opposite', icon: Copy, action: () => {
            copyJointToOpposite(jointName);
            trackEditorEvent('joint_context_action', { joint: jointName, action: 'copy_to' });
          } });
          menuItems.push({ label: 'Copy from Opposite', icon: ArrowLeftRight, action: () => {
            copyJointFromOpposite(jointName);
            trackEditorEvent('joint_context_action', { joint: jointName, action: 'copy_from' });
          } });
        }
        openContextMenu(originalEvent.clientX, originalEvent.clientY, menuItems);
      }}
    >
      <mesh scale={scale} position={posFront} geometry={SPHERE_GEO}>
        {isSelected ? (
          <primitive object={SELECTED_MAT} attach="material" />
        ) : isAffectedParent ? (
          <primitive object={PARENT_HIGHLIGHT_MAT} attach="material" />
        ) : hovered ? (
          <primitive object={HOVER_MAT} attach="material" />
        ) : (
          <meshPhysicalMaterial color={colorFront} {...SKIN_PROPS} />
        )}
      </mesh>
      <mesh scale={scale} position={posBack} geometry={SPHERE_GEO}>
        {isSelected ? (
          <primitive object={SELECTED_MAT} attach="material" />
        ) : isAffectedParent ? (
          <primitive object={PARENT_HIGHLIGHT_MAT} attach="material" />
        ) : hovered ? (
          <primitive object={HOVER_MAT} attach="material" />
        ) : (
          <meshPhysicalMaterial color={colorBack} {...(colorBack === HAIR_COLOR ? HAIR_PROPS : SKIN_PROPS)} />
        )}
      </mesh>

      {/* Hover Visual Indicator for Ovals */}
      {hovered && (
        <group>
           <Billboard>
              <mesh scale={Math.max(scale[0], scale[1], scale[2])} geometry={RING_GEO}>
                <primitive object={HOVER_INDICATOR_MAT} attach="material" />
              </mesh>
           </Billboard>
           <mesh scale={scale.map(s => s * 1.1) as [number, number, number]} geometry={SPHERE_GEO}>
              <primitive object={HOVER_GLOW_MAT} attach="material" />
           </mesh>
        </group>
      )}

      {isTutorialTarget && (
        <group>
          <Html center pointerEvents="none" style={{ pointerEvents: 'none' }}>
             <div id="tutorial-3d-target" style={{ width: '1px', height: '1px', pointerEvents: 'none' }} />
          </Html>
          <mesh rotation={[Math.PI / 2, 0, 0]} scale={[scale[0] * 1.5, scale[1] * 1.5, scale[2] * 1.5]} raycast={() => null}>
            <primitive object={TORUS_GEO} attach="geometry" />
            <meshBasicMaterial color="#f59e0b" transparent opacity={0.8} />
          </mesh>
        </group>
      )}

      {isSelected && !hideGizmo && showAxes && (
        <Suspense fallback={null}>
          <LocalAxesWidget jointName={jointName} />
        </Suspense>
      )}
    </group>
  );
});

const ProxyLabel: React.FC<{ text: string; show: boolean; position?: [number, number, number]; jointName?: string }> = React.memo(({ text, show, position = [0, 1.5, 0], jointName }) => {
  if (!show) return null;

  const isLeftOfJoint = position[0] < 0;
  const isRightOfJoint = position[0] > 0;

  let transformValue = 'translateY(-50%)';
  if (isLeftOfJoint) {
    transformValue = 'translate(-100%, -50%)';
  } else if (!isRightOfJoint) {
    transformValue = 'translate(-50%, -50%)';
  }

  return (
    <group>
      <Line
        points={[[0, 0, 0], position] as [number, number, number][]}
        color="#fde047"
        lineWidth={1}
        transparent
        opacity={0.4}
      />
      <Html position={position as [number, number, number]} pointerEvents="none">
        <div 
          id={jointName ? `label-${jointName}` : undefined}
          style={{
          background: 'rgba(15, 23, 42, 0.95)',
          color: '#fde047',
          padding: '2px 10px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          border: '1px solid #475569',
          boxShadow: '0 8px 16px rgba(0,0,0,0.5)',
          pointerEvents: 'none',
          userSelect: 'none',
          transform: transformValue,
          marginLeft: isRightOfJoint ? '4px' : '0',
          marginRight: isLeftOfJoint ? '4px' : '0'
        }}>
          {text}
        </div>
      </Html>
    </group>
  );
});

const LocalAxesWidget: React.FC<{ jointName?: string }> = React.memo(({ jointName }) => {
  const isRightSide = !!jointName?.startsWith('right');
  const [keys, setKeys] = React.useState({ x: false, y: false, z: false, a: false, s: false, d: false, f: false });
  const limits = jointName ? JOINT_CONSTRAINTS[jointName] : null;
  const hasX = jointName === 'orientation' ? false : (!limits || limits.x[0] !== limits.x[1]);
  const hasY = !limits || limits.y[0] !== limits.y[1];
  const hasZ = jointName === 'orientation' ? false : (!limits || limits.z[0] !== limits.z[1]);

  // One ref per axis so each group can be placed in its actual Euler gimbal frame
  const xGroupRef = React.useRef<THREE.Group>(null);
  const yGroupRef = React.useRef<THREE.Group>(null);
  const zGroupRef = React.useRef<THREE.Group>(null);
  // All pre-allocated to avoid per-frame GC
  const _q0    = React.useMemo(() => new THREE.Quaternion(), []);
  const _q1    = React.useMemo(() => new THREE.Quaternion(), []);
  const _q2    = React.useMemo(() => new THREE.Quaternion(), []);
  const _qFull = React.useMemo(() => new THREE.Quaternion(), []);
  const _qInv  = React.useMemo(() => new THREE.Quaternion(), []);
  const _qTmp  = React.useMemo(() => new THREE.Quaternion(), []);
  const _aX    = React.useMemo(() => new THREE.Vector3(1, 0, 0), []);
  const _aY    = React.useMemo(() => new THREE.Vector3(0, 1, 0), []);
  const _aZ    = React.useMemo(() => new THREE.Vector3(0, 0, 1), []);

  useFrame(() => {
    if (!jointName) return;
    const rot = (useDanceStore.getState().currentPose[jointName] as JointRotation) || [0, 0, 0];
    const order = getJointRotationOrder(jointName);
    const avecs = { x: _aX, y: _aY, z: _aZ };
    const axisRefs: Record<string, React.RefObject<THREE.Group | null>> = { x: xGroupRef, y: yGroupRef, z: zGroupRef };
    const deg = THREE.MathUtils.degToRad;

    const a0 = order[0].toLowerCase() as 'x' | 'y' | 'z';
    const a1 = order[1].toLowerCase() as 'x' | 'y' | 'z';
    const a2 = order[2].toLowerCase() as 'x' | 'y' | 'z';
    const angles = { x: deg(rot[0]), y: deg(rot[1]), z: deg(rot[2]) };

    _q0.setFromAxisAngle(avecs[a0], angles[a0]);
    _q1.setFromAxisAngle(avecs[a1], angles[a1]);
    _q2.setFromAxisAngle(avecs[a2], angles[a2]);

    // THREE.js Euler XYZ: Q = Q0·Q1·Q2 (first axis leftmost), so Q_full = Qx·Qy·Qz
    _qFull.copy(_q0).multiply(_q1).multiply(_q2);
    _qInv.copy(_qFull).invert();

    // Each ring lives in its actual Euler frame; labels follow inside their group
    if (axisRefs[a0].current) axisRefs[a0].current.quaternion.copy(_qInv);
    if (axisRefs[a1].current) axisRefs[a1].current.quaternion.copy(_qInv).multiply(_q0);
    _qTmp.copy(_q0).multiply(_q1);
    if (axisRefs[a2].current) axisRefs[a2].current.quaternion.copy(_qInv).multiply(_qTmp);
  });

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.key) return;
      const isFeedbackOpen = useDanceStore.getState().isFeedbackOpen;
      const target = e.target as HTMLElement;
      if (isFeedbackOpen || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      const k = e.key.toLowerCase();
      const isForbidden = jointName === 'orientation' && (k === 'x' || k === 'z');
      if (['x', 'y', 'z', 'a', 's', 'd', 'f'].includes(k) && !isForbidden) setKeys(s => ({ ...s, [k]: true }));
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.key) return;
      const k = e.key.toLowerCase();
      setKeys(s => ({ ...s, [k]: false }));
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [jointName]);

  const scale = 3.0;
  const activeColor = '#fb923c'; 

  const RotationRing = ({ color }: { color: string }) => (
    <group>
      <mesh>
        <torusGeometry args={[0.1, 0.005, 8, 24, Math.PI * 1.5]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <mesh position={[0, -0.1, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <coneGeometry args={[0.015, 0.06, 8]} />
        <meshBasicMaterial color={color} />
      </mesh>
    </group>
  );
  const MoveIndicator = ({ color }: { color: string }) => (
    <group scale={0.6}>
      {[0, Math.PI / 2, Math.PI, Math.PI * 1.5].map((rot, i) => (
        <group key={i} rotation={[0, rot, 0]}>
          <mesh position={[0, 0, 0.15]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.006, 0.006, 0.15, 8]} />
            <meshBasicMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, 0.25]} rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.025, 0.06, 8]} />
            <meshBasicMaterial color={color} />
          </mesh>
        </group>
      ))}
    </group>
  );

  const AttitudeIndicator = ({ color }: { color: string }) => (
    <group scale={0.7}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.25, 0.006, 8, 32]} />
        <meshBasicMaterial color={color} transparent opacity={0.6} />
      </mesh>
      <group rotation={[0, 0, Math.PI / 2]}>
        <mesh rotation={[0, Math.PI / 2, 0]}>
          <torusGeometry args={[0.25, 0.006, 8, 32]} />
          <meshBasicMaterial color={color} transparent opacity={0.6} />
        </mesh>
      </group>
    </group>
  );
  return (
    <group scale={[scale, scale, scale]}>
      {hasX && (
        <group ref={xGroupRef}>
          <group rotation-y={isRightSide ? Math.PI : 0}>
            <mesh position={[0.3, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
              <cylinderGeometry args={[0.005, 0.005, 0.6, 8]} />
              <meshBasicMaterial color={keys.x ? activeColor : '#ef4444'} />
            </mesh>
            <group position={[0.45, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
              <RotationRing color={keys.x ? activeColor : '#ef4444'} />
            </group>
          </group>
          <Text position={[0.85, 0, 0]} fontSize={0.12} anchorX="center" anchorY="middle" renderOrder={999} depthOffset={-1}>
            <meshBasicMaterial color={keys.x ? activeColor : '#ef4444'} depthTest={false} />
            X
          </Text>
        </group>
      )}
      {hasY && (
        <group ref={yGroupRef}>
          <mesh position={[0, 0.15, 0]}>
            <cylinderGeometry args={[0.005, 0.005, jointName === 'orientation' ? 0.3 : 0.6, 8]} />
            <meshBasicMaterial color={keys.y ? activeColor : '#22c55e'} />
          </mesh>
          <group position={[0, jointName === 'orientation' ? 0.25 : 0.45, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <RotationRing color={keys.y ? activeColor : '#22c55e'} />
          </group>
          <Text position={[0, 0.85, 0]} fontSize={0.12} anchorX="center" anchorY="middle" renderOrder={999} depthOffset={-1}>
            <meshBasicMaterial color={keys.y ? activeColor : '#22c55e'} depthTest={false} />
            Y
          </Text>
        </group>
      )}
      {hasZ && (
        <group ref={zGroupRef}>
          <mesh position={[0, 0, 0.3]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.005, 0.005, 0.6, 8]} />
            <meshBasicMaterial color={keys.z ? activeColor : '#3b82f6'} />
          </mesh>
          <group position={[0, 0, 0.45]}>
            <RotationRing color={keys.z ? activeColor : '#3b82f6'} />
          </group>
          <Text position={[0, 0, 0.85]} fontSize={0.12} anchorX="center" anchorY="middle" renderOrder={999} depthOffset={-1}>
            <meshBasicMaterial color={keys.z ? activeColor : '#3b82f6'} depthTest={false} />
            Z
          </Text>
        </group>
      )}
      {jointName === 'orientation' && (
        <>
          <group scale={0.7} position={[0.4, 0, 0.4]}>
            <AttitudeIndicator color={keys.a ? activeColor : SELECTED_COLOR} />
            <Html position={[0.2, 0, 0.2]} center>
              <div style={{ color: keys.a ? activeColor : SELECTED_COLOR, fontSize: '10px', fontWeight: 'bold', background: 'rgba(0,0,0,0.5)', padding: '1px 3px', borderRadius: '3px', boxShadow: keys.a ? `0 0 10px ${activeColor}` : 'none', border: keys.a ? `1px solid ${activeColor}` : '1px solid transparent' }}>A</div>
            </Html>
          </group>
          <group scale={0.7} position={[-0.4, 0, 0.4]}>
            <MoveIndicator color={keys.d ? activeColor : '#38bdf8'} />
            <Html position={[-0.2, 0, 0.2]} center>
              <div style={{ color: keys.d ? activeColor : '#38bdf8', fontSize: '10px', fontWeight: 'bold', background: 'rgba(0,0,0,0.5)', padding: '1px 3px', borderRadius: '3px', boxShadow: keys.d ? `0 0 10px ${activeColor}` : 'none', border: keys.d ? `1px solid ${activeColor}` : '1px solid transparent' }}>D</div>
            </Html>
          </group>
          <group scale={0.7} position={[0, 0.4, 0.4]}>
            <AttitudeIndicator color={keys.f ? activeColor : '#c084fc'} />
            <Html position={[0, 0.2, 0.2]} center>
              <div style={{ color: keys.f ? activeColor : '#c084fc', fontSize: '10px', fontWeight: 'bold', background: 'rgba(0,0,0,0.5)', padding: '1px 3px', borderRadius: '3px', boxShadow: keys.f ? `0 0 10px ${activeColor}` : 'none', border: keys.f ? `1px solid ${activeColor}` : '1px solid transparent' }}>F</div>
            </Html>
          </group>
        </>
      )}
    </group>
  );
});

interface BoneGroupProps {
  jointName: string;
  position: [number, number, number];
  children: React.ReactNode;
  propPose?: Pose;
}

const BoneGroup: React.FC<BoneGroupProps> = ({ jointName, position, children, propPose }) => {
  const groupRef = React.useRef<THREE.Group>(null);
  const order = useMemo(() => getJointRotationOrder(jointName), [jointName]);
  
  // High-performance rotation update bypassing React re-renders
  useFrame(() => {
    if (propPose || !groupRef.current) return;
    const rotation = (useDanceStore.getState().currentPose[jointName] as JointRotation) || [0, 0, 0];
    groupRef.current.rotation.set(
      THREE.MathUtils.degToRad(rotation[0]),
      THREE.MathUtils.degToRad(rotation[1]),
      THREE.MathUtils.degToRad(rotation[2]),
      order as any
    );
  });

  // For propPose (Library/Preview), use static rotation
  const propRotation = propPose?.[jointName] as JointRotation | undefined;

  return (
    <group 
      ref={groupRef}
      name={jointName}
      position={position} 
      rotation={propRotation ? [
        THREE.MathUtils.degToRad(propRotation[0]),
        THREE.MathUtils.degToRad(propRotation[1]),
        THREE.MathUtils.degToRad(propRotation[2])
      ] : undefined}
      rotation-order={order as any}
    >
      {children}
    </group>
  );
};

export function PrimitiveMannequin({ pose: propPose, ...props }: { pose?: Pose } & any) {
  const { t } = useTranslation();
  const groupRef = React.useRef<THREE.Group>(null);

  React.useEffect(() => {
    groupRef.current?.traverse(obj => {
      if (obj instanceof THREE.Mesh && obj.parent?.name !== 'torso-disc') obj.castShadow = true;
    });
  }, []);

  // No longer subscribing to state.currentPose here!
  // Subscriptions are moved to granular BoneGroup components.

  const isReadOnly = useDanceStore((state: DanceState) => state.isReadOnly);
  const showLabels = useDanceStore((state: DanceState) => !propPose && state.showLabels); 
  const selectedJoint = useDanceStore((state: DanceState) => (propPose || state.isReadOnly) ? null : state.selectedJoint);
  const setSelectedJoint = useDanceStore((state: DanceState) => (propPose || state.isReadOnly) ? undefined : state.setSelectedJoint);
  const isMirroring = useDanceStore((state: DanceState) => propPose ? false : state.isMirroring);

  const dimensions = useMemo(() => MANNEQUIN_DIMENSIONS, []);
  const headTopY = dimensions.torsoBase[1]
    + dimensions.pelvicHeight
    + dimensions.torsoHeight
    + dimensions.neckLen
    + dimensions.headOffset[1]
    + dimensions.headDim[1] * 2;
  const attitudeOrbY = headTopY + 0.6;

  const clothingEnabled = useStorageStore((state: StorageState) => state.preferences.clothingEnabled);
  const mannequinGender = useStorageStore((state: StorageState) => state.preferences.mannequinGender);

  const tutorialProgress = useStorageStore((state: StorageState) => state.tutorialProgress);
  const currentStepId = useMemo(() => {
    const currentModule = TUTORIAL_MODULES.find(m => m.id === tutorialProgress.currentModuleId);
    return currentModule?.steps[tutorialProgress.currentStepIndex]?.id || null;
  }, [tutorialProgress]);

  const tutorialTargetJoint = useMemo(() => {
    const currentModule = TUTORIAL_MODULES.find(m => m.id === tutorialProgress.currentModuleId);
    const currentStep = currentModule?.steps[tutorialProgress.currentStepIndex];
    if (currentStep?.actionRequired === 'selectJoint' || 
        currentStep?.actionRequired === 'rotateJoint' || 
        currentStep?.actionRequired === 'rotateJointMouse' ||
        currentStep?.actionRequired === 'rotateConicalMouse' ||
        currentStep?.actionRequired === 'rotateTorsoMouse' ||
        currentStep?.actionRequired === 'moveFloorMouse' ||
        (currentStep?.actionRequired === 'next' && currentStep?.targetId === 'tutorial-3d-target')) {
      return currentStep?.targetValue;
    }
    return null;
  }, [tutorialProgress]);

  const sharedProps = { 
    selectedJoint, 
    setSelectedJoint, 
    isMirroring,
    getIsTutorialTarget: (name: string) => tutorialTargetJoint === name
  };

  return (
    <group ref={groupRef} {...props}>
      <group position={[0, attitudeOrbY, 0]}>
        <ProxyJoint jointName="orientation" color={JOINT_COLOR} size={0.22} isTutorialTarget={sharedProps.getIsTutorialTarget('orientation')} {...sharedProps} />
        <ProxyLabel text={t('sidebar.vertical_attitude')} show={showLabels} position={[-8.0, 3.0, 0]} />
      </group>
      <BoneGroup jointName="pelvis" position={dimensions.torsoBase} propPose={propPose}>
        {clothingEnabled && <MannequinClothing gender={mannequinGender} part="pelvis" />}
        <BoneGroup jointName="leftHip" position={dimensions.hipOffset} propPose={propPose}>
          {clothingEnabled && <MannequinClothing gender={mannequinGender} part="leftHip" />}
          <ProxyJoint jointName="leftHip" isTutorialTarget={sharedProps.getIsTutorialTarget('leftHip')} {...sharedProps} />
          <ProxyLabel text={t('sidebar.hip')} jointName="leftHip" show={showLabels} position={[3.5, 0, 0]} />
          <ProxyBone from={[0, 0, 0]} to={[0, -dimensions.upperLegLen, 0]} radiusTop={0.15} radiusBottom={0.2} />
          <BoneGroup jointName="leftKnee" position={[0, -dimensions.upperLegLen, 0]} propPose={propPose}>
            <ProxyJoint color={JOINT_COLOR} jointName="leftKnee" isTutorialTarget={sharedProps.getIsTutorialTarget('leftKnee')} {...sharedProps} />
            <ProxyLabel text={t('sidebar.knee')} jointName="leftKnee" show={showLabels} position={[3.5, -2.0, 0]} />
            <ProxyBone from={[0, 0, 0]} to={[0, -dimensions.lowerLegLen, 0]} radiusTop={0.1} radiusBottom={0.15} />
            <BoneGroup jointName="leftFoot" position={[0, -dimensions.lowerLegLen, 0]} propPose={propPose}>
              <ProxyJoint color={JOINT_COLOR} size={0.12} jointName="leftFoot" isTutorialTarget={sharedProps.getIsTutorialTarget('leftFoot')} {...sharedProps} />
              <ProxyLabel text={t('sidebar.foot')} jointName="leftFoot" show={showLabels} position={[3.5, 0, 1.5]} />
              <ProxyOval scale={dimensions.footDim} offset={[0, 0, dimensions.footDim[2]]} separationAxis="Y" jointName="leftFoot" hideGizmo isTutorialTarget={sharedProps.getIsTutorialTarget('leftFoot')} {...sharedProps} />
            </BoneGroup>
          </BoneGroup>
        </BoneGroup>

        <BoneGroup jointName="rightHip" position={[-dimensions.hipOffset[0], dimensions.hipOffset[1], dimensions.hipOffset[2]]} propPose={propPose}>
          {clothingEnabled && <MannequinClothing gender={mannequinGender} part="rightHip" />}
          <ProxyJoint color={JOINT_COLOR} jointName="rightHip" isTutorialTarget={sharedProps.getIsTutorialTarget('rightHip')} {...sharedProps} />
          <ProxyBone from={[0, 0, 0]} to={[0, -dimensions.upperLegLen, 0]} radiusTop={0.15} radiusBottom={0.2} />
          <BoneGroup jointName="rightKnee" position={[0, -dimensions.upperLegLen, 0]} propPose={propPose}>
            <ProxyJoint color={JOINT_COLOR} jointName="rightKnee" isTutorialTarget={sharedProps.getIsTutorialTarget('rightKnee')} {...sharedProps} />
            <ProxyBone from={[0, 0, 0]} to={[0, -dimensions.lowerLegLen, 0]} radiusTop={0.1} radiusBottom={0.15} />
            <BoneGroup jointName="rightFoot" position={[0, -dimensions.lowerLegLen, 0]} propPose={propPose}>
              <ProxyJoint color={JOINT_COLOR} size={0.18} jointName="rightFoot" isTutorialTarget={sharedProps.getIsTutorialTarget('rightFoot')} {...sharedProps} />
              <ProxyOval scale={dimensions.footDim} offset={[0, 0, dimensions.footDim[2]]} separationAxis="Y" jointName="rightFoot" hideGizmo isTutorialTarget={sharedProps.getIsTutorialTarget('rightFoot')} {...sharedProps} />
            </BoneGroup>
          </BoneGroup>
        </BoneGroup>

        {/* Pelvic triangle: solid filled panel + edge bones */}
        <PelvicTriangle hipX={dimensions.hipOffset[0]} apexY={dimensions.pelvicHeight} />
        <ProxyBone from={[dimensions.hipOffset[0], 0, 0]} to={[-dimensions.hipOffset[0], 0, 0]} radiusTop={0.08} radiusBottom={0.08} />
        <ProxyBone from={[dimensions.hipOffset[0], 0, 0]} to={[0, dimensions.pelvicHeight, 0]} radiusTop={0.08} radiusBottom={0.08} />
        <ProxyBone from={[-dimensions.hipOffset[0], 0, 0]} to={[0, dimensions.pelvicHeight, 0]} radiusTop={0.08} radiusBottom={0.08} />

        <BoneGroup jointName="torso" position={[0, dimensions.pelvicHeight, 0]} propPose={propPose}>
          {clothingEnabled && <MannequinClothing gender={mannequinGender} part="torso" />}
          <ProxyJoint color={JOINT_COLOR} size={0.25} jointName="torso" isTutorialTarget={sharedProps.getIsTutorialTarget('torso')} {...sharedProps} />
          <ProxyLabel text={t('sidebar.torso')} jointName="torso" show={showLabels} position={[-7.0, 0.0, 0]} />
          <ProxyBone from={[0, 0, 0]} to={[0, dimensions.stomachHeight, 0]} color={HI_COLOR} radiusTop={0.2} radiusBottom={0.2} />
          <BoneGroup jointName="stomach" position={[0, dimensions.stomachHeight, 0]} propPose={propPose}>
            {clothingEnabled && <MannequinClothing gender={mannequinGender} part="stomach" />}
            <ProxyJoint color={JOINT_COLOR} size={0.25} jointName="stomach" isTutorialTarget={sharedProps.getIsTutorialTarget('stomach')} {...sharedProps} />
            <ProxyLabel text={t('sidebar.stomach')} jointName="stomach" show={showLabels} position={[-6.0, 1.0, 0]} />
            <ProxyBone from={[0, 0, 0]} to={[0, dimensions.chestOffset, 0]} color={HI_COLOR} radiusTop={0.2} radiusBottom={0.2} />
            <BoneGroup jointName="chest" position={[0, dimensions.chestOffset, 0]} propPose={propPose}>
              {clothingEnabled && <MannequinClothing gender={mannequinGender} part="chest" />}
              <ProxyJoint color={JOINT_COLOR} size={0.25} jointName="chest" isTutorialTarget={sharedProps.getIsTutorialTarget('chest')} {...sharedProps} />
              <ProxyLabel text={t('sidebar.chest')} jointName="chest" show={showLabels} position={[-6.0, 1.0, 0]} />
              <ProxyBone from={[0, 0, 0]} to={[0, dimensions.torsoHeight - dimensions.stomachHeight - dimensions.chestOffset, 0]} color={HI_COLOR} radiusTop={0.2} radiusBottom={0.2} />
              <group position={[0, dimensions.torsoHeight - dimensions.stomachHeight - dimensions.chestOffset, 0]}>
              <mesh scale={[0.15, 0.3, 0.15]}>
                <primitive object={CYLINDER_GEO} attach="geometry" />
                <meshPhysicalMaterial color={HI_COLOR} {...SKIN_PROPS} />
              </mesh>
              <ProxyBone from={[0, 0, 0]} to={[0, dimensions.neckLen, 0]} />
              <BoneGroup jointName="neck" position={[0, dimensions.neckLen, 0]} propPose={propPose}>
                <ProxyLabel text={t('sidebar.neck')} jointName="neck" show={showLabels} position={[-5.0, 2.0, 0]} />
                <ProxyJoint color={JOINT_COLOR} size={0.2} jointName="neck" isTutorialTarget={sharedProps.getIsTutorialTarget('neck')} {...sharedProps} />
                <ProxyBone from={[0, 0, 0]} to={dimensions.headOffset} />
                <BoneGroup jointName="head" position={dimensions.headOffset} propPose={propPose}>
                    <ProxyLabel text={t('sidebar.head')} jointName="head" show={showLabels} position={[-4.0, 3.0, 0]} />
                    <ProxyOval scale={dimensions.headDim} offset={[0, dimensions.headDim[1], 0]} jointName="head" colorBack={HAIR_COLOR} isTutorialTarget={sharedProps.getIsTutorialTarget('head')} {...sharedProps} />
                    <group position={[0, dimensions.headDim[1] * 1.05, -0.05]}>
                      <mesh rotation={[-0.5, 0, 0]} position={[0, 0.1, 0.05]} scale={[dimensions.headDim[0] * 1.05, dimensions.headDim[1] * 1.05, dimensions.headDim[2] * 1.05]}>
                        <sphereGeometry args={[1, 16, 16, 0, Math.PI * 2, 0, Math.PI * 0.55]} />
                        <primitive object={HAIR_MAT} attach="material" />
                      </mesh>
                      <mesh position={[0, -0.1, -0.1]} scale={[dimensions.headDim[0] * 1.06, dimensions.headDim[1] * 0.8, dimensions.headDim[2] * 1.06]}>
                        <primitive object={SPHERE_GEO} attach="geometry" />
                        <primitive object={HAIR_MAT} attach="material" />
                      </mesh>
                    </group>
                    <group position={[0, dimensions.headDim[1], dimensions.headDim[2] * 1.1]}>
                      <mesh position={[0.2, 0.1, 0]} scale={0.07}><primitive object={SPHERE_GEO} attach="geometry" /><meshStandardMaterial color="#2c1e14" roughness={0.4} /></mesh>
                      <mesh position={[-0.2, 0.1, 0]} scale={0.07}><primitive object={SPHERE_GEO} attach="geometry" /><meshStandardMaterial color="#2c1e14" roughness={0.4} /></mesh>
                    </group>
                  </BoneGroup>
                </BoneGroup>
              <BoneGroup jointName="leftClavicle" position={[dimensions.shoulderWidth / 4, 0, 0]} propPose={propPose}>
                <ProxyLabel text={t('sidebar.shrug')} jointName="leftClavicle" show={showLabels} position={[3.1, 2.4, 0]} />
                <ProxyJoint color={JOINT_COLOR} size={0.2} jointName="leftClavicle" isTutorialTarget={sharedProps.getIsTutorialTarget('leftClavicle')} {...sharedProps} />
                <ProxyBone from={[0, 0, 0]} to={[dimensions.shoulderWidth * 3/4, 0, 0]} />
                 <BoneGroup jointName="leftShoulder" position={[dimensions.shoulderWidth * 3/4, 0, 0]} propPose={propPose}>
                  {clothingEnabled && <MannequinClothing gender={mannequinGender} part="leftShoulder" />}
                  <ProxyJoint color={JOINT_COLOR} size={0.2} jointName="leftShoulder" isTutorialTarget={sharedProps.getIsTutorialTarget('leftShoulder')} {...sharedProps} />
                  {currentStepId === 'adv-drag-relationship-intro' && (
                    <group>
                      <Html center pointerEvents="none" style={{ pointerEvents: 'none' }}>
                        <div id="tutorial-parent-joint-target" style={{ width: '1px', height: '1px', pointerEvents: 'none' }} />
                      </Html>
                      <mesh rotation={[Math.PI / 2, 0, 0]} scale={0.2 * 1.5} raycast={() => null}>
                        <primitive object={TORUS_GEO} attach="geometry" />
                        <meshBasicMaterial color="#f87171" transparent opacity={0.8} />
                      </mesh>
                    </group>
                  )}
                  <ProxyLabel text={t('sidebar.shoulder')} jointName="leftShoulder" show={showLabels} position={[3.5, 0, 0]} />
                  <ProxyBone from={[0, 0, 0]} to={[0, -dimensions.upperArmLen, 0]} radiusTop={0.15} radiusBottom={0.2} />
                  <BoneGroup jointName="leftElbow" position={[0, -dimensions.upperArmLen, 0]} propPose={propPose}>
                    <ProxyJoint color={JOINT_COLOR} size={0.2} jointName="leftElbow" isTutorialTarget={sharedProps.getIsTutorialTarget('leftElbow')} {...sharedProps} />
                    {currentStepId === 'adv-drag-relationship-intro' && (
                      <group>
                        <Html center pointerEvents="none" style={{ pointerEvents: 'none' }}>
                          <div id="tutorial-child-joint-target" style={{ width: '1px', height: '1px', pointerEvents: 'none' }} />
                        </Html>
                        <mesh rotation={[Math.PI / 2, 0, 0]} scale={0.2 * 1.5} raycast={() => null}>
                          <primitive object={TORUS_GEO} attach="geometry" />
                          <meshBasicMaterial color="#38bdf8" transparent opacity={0.8} />
                        </mesh>
                      </group>
                    )}
                    <ProxyLabel text={t('sidebar.elbow')} jointName="leftElbow" show={showLabels} position={[1.75, 0, 0]} />
                    <ProxyBone from={[0, 0, 0]} to={[0, -dimensions.lowerArmLen, 0]} radiusTop={0.1} radiusBottom={0.15} />
                    <BoneGroup jointName="leftPalm" position={[0, -dimensions.lowerArmLen, 0]} propPose={propPose}>
                      <ProxyJoint color={JOINT_COLOR} size={0.16} jointName="leftPalm" isTutorialTarget={sharedProps.getIsTutorialTarget('leftPalm')} {...sharedProps} />
                      <ProxyLabel text={t('sidebar.palm')} jointName="leftPalm" show={showLabels} position={[3.5, -1.5, 0]} />
                      <ProxyOval scale={dimensions.palmDim} offset={[0, -dimensions.palmDim[1], 0]} jointName="leftPalm" hideGizmo isTutorialTarget={sharedProps.getIsTutorialTarget('leftPalm')} {...sharedProps} />
                    </BoneGroup>
                  </BoneGroup>
                </BoneGroup>
              </BoneGroup>
              <BoneGroup jointName="rightClavicle" position={[-dimensions.shoulderWidth / 4, 0, 0]} propPose={propPose}>
                <ProxyJoint color={JOINT_COLOR} size={0.2} jointName="rightClavicle" isTutorialTarget={sharedProps.getIsTutorialTarget('rightClavicle')} {...sharedProps} />
                <ProxyBone from={[0, 0, 0]} to={[-dimensions.shoulderWidth * 3/4, 0, 0]} />
                <BoneGroup jointName="rightShoulder" position={[-dimensions.shoulderWidth * 3/4, 0, 0]} propPose={propPose}>
                  {clothingEnabled && <MannequinClothing gender={mannequinGender} part="rightShoulder" />}
                  <ProxyJoint color={JOINT_COLOR} jointName="rightShoulder" isTutorialTarget={sharedProps.getIsTutorialTarget('rightShoulder')} {...sharedProps} />
                  <ProxyBone from={[0, 0, 0]} to={[0, -dimensions.upperArmLen, 0]} radiusTop={0.15} radiusBottom={0.2} />
                  <BoneGroup jointName="rightElbow" position={[0, -dimensions.upperArmLen, 0]} propPose={propPose}>
                    <ProxyJoint color={JOINT_COLOR} size={0.2} jointName="rightElbow" isTutorialTarget={sharedProps.getIsTutorialTarget('rightElbow')} {...sharedProps} />
                    <ProxyBone from={[0, 0, 0]} to={[0, -dimensions.lowerArmLen, 0]} radiusTop={0.1} radiusBottom={0.15} />
                    <BoneGroup jointName="rightPalm" position={[0, -dimensions.lowerArmLen, 0]} propPose={propPose}>
                      <ProxyJoint color={JOINT_COLOR} size={0.16} jointName="rightPalm" isTutorialTarget={sharedProps.getIsTutorialTarget('rightPalm')} {...sharedProps} />
                      <ProxyOval scale={dimensions.palmDim} offset={[0, -dimensions.palmDim[1], 0]} jointName="rightPalm" hideGizmo isTutorialTarget={sharedProps.getIsTutorialTarget('rightPalm')} {...sharedProps} />
                    </BoneGroup>
                  </BoneGroup>
                </BoneGroup>
              </BoneGroup>
              </group>
            </BoneGroup>
          </BoneGroup>
        </BoneGroup>
      </BoneGroup>
    </group>
  );
}
